import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalyticsContext } from './analyticsContext';

/**
 * Path → page title mapping for known routes.
 * Covers all routes in router.tsx.
 */
const PATH_TITLES: Record<string, string> = {
  '/': 'Home',
  '/select-diners': 'Select Party Size',
  '/select-date': 'Select Date',
  '/full-calendar': 'Calendar',
  '/select-service': 'Select Service',
  '/early-access': 'Early Access',
  '/select-menu-filter': 'Menu Filter',
  '/select-area-filter': 'Area Filter',
  '/select-experience': 'Select Experience',
  '/select-area': 'Select Area',
  '/select-time': 'Select Time',
  '/sign-up': 'Sign Up',
  '/sign-in': 'Sign In',
  '/auth-return': 'Auth Return',
  '/complete-profile': 'Complete Profile',
  '/waiting-list': 'Waiting List',
  '/waiting-list-calendar': 'Waiting List Calendar',
  '/waiting-list-alternatives': 'Waiting List Alternatives',
  '/maridaje': 'Pairings',
  '/personalize': 'Personalize',
  '/payment': 'Payment',
  '/confirmation': 'Confirmation',
  '/booking-final': 'Booking Final',
  '/confirm-early-access': 'Confirm Early Access',
  '/confirm-guarantee': 'Confirm Guarantee',
  '/gift-card': 'Gift Card',
  '/profile': 'Profile',
  '/reserva': 'Reserva',
  '/book': 'Book',
  '/staff': 'Staff Booking',
  '/platform': 'Platform',
};

function getTitleForPath(pathname: string): string {
  // Exact match first
  if (PATH_TITLES[pathname]) return PATH_TITLES[pathname];

  // Dynamic route matching (e.g., /reset/:token, /reservation-status/:token)
  if (pathname.startsWith('/reset/')) return 'Reset Password';
  if (pathname.startsWith('/reservation-status/')) return 'Reservation Status';
  if (pathname.startsWith('/redeem-gift-card/')) return 'Redeem Gift Card';
  if (pathname.startsWith('/review/')) return 'Review';
  if (pathname.startsWith('/unsubscribe/')) return 'Unsubscribe';
  if (pathname.startsWith('/search/')) return 'Deep Link';
  if (pathname.startsWith('/waiting-list-alternative/')) return 'WL Alternative Link';

  return pathname;
}

/**
 * Renderless component that tracks page views on route changes.
 * Uses useLocation() so it fires regardless of how navigation happens
 * (goToNext, useNavigate, Link, browser back/forward).
 */
export function RouteTracker() {
  const location = useLocation();
  const { trackPageView } = useAnalyticsContext();
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Guard against duplicate fires (React strict mode double-mount)
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    trackPageView({
      path: location.pathname,
      title: getTitleForPath(location.pathname),
    });
  }, [location.pathname, trackPageView]);

  return null;
}
